import {FC, useCallback, useEffect, useState} from 'react';
import {styled} from 'styled-components';

import {Lane} from '@shared/model';

import {ButtonAsLink} from '@shared-web/components/core/button';
import {LoadingIndicator} from '@shared-web/components/core/loading_indicator';
import {notifyError} from '@shared-web/lib/notification';
import {useStringArrayQueryString} from '@shared-web/lib/use_query_string';

import {ChatGptMarkdown} from '@src/components/chat_gpt_markdown';
import {apiCall} from '@src/lib/api';
import {Team} from '@src/lib/teams';

interface LaneOverviewProps {
  lane: Lane;
}

export const LaneOverview: FC<LaneOverviewProps> = props => {
  const {lane} = props;
  const [teamBlue] = useStringArrayQueryString(Team.Blue, []);
  const [teamRed] = useStringArrayQueryString(Team.Red, []);
  const [laneDescription, setLaneDescription] = useState<string>();
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Fetch information about the teams champions
  useEffect(() => {
    setLaneDescription(undefined);
    apiCall('POST /gpt-lane-description', {lane, teamBlue, teamRed})
      .then(({data}) => setLaneDescription(data))
      .catch(notifyError);
  }, [lane, teamBlue, teamRed]);

  const toggleExpand = useCallback(() => setIsCollapsed(curr => !curr), []);

  return (
    <Wrapper>
      {laneDescription !== undefined ? (
        <Content $collapsed={isCollapsed}>
          <ChatGptMarkdown message={laneDescription} />
          <FloatingButton>
            <ButtonAsLink onClick={toggleExpand} overrides={{fontSize: 12, fontWeight: 'bold'}}>
              {isCollapsed ? 'EXPAND' : 'COLLAPSE'}
            </ButtonAsLink>
          </FloatingButton>
        </Content>
      ) : (
        <LoadingIndicator />
      )}
    </Wrapper>
  );
};

LaneOverview.displayName = 'LaneOverview';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  background: #3737438c;
  border-radius: 10px;
`;

const Content = styled.div<{$collapsed: boolean}>`
  position: relative;
  ${p =>
    p.$collapsed &&
    `
      max-height: 128px;
      overflow: hidden;
  `}
  padding: 6px 11px;
`;

const FloatingButton = styled.div`
  position: absolute;
  bottom: 4px;
  left: 50%;
  translate: -50% 0;
  background: #000000aa;
  padding: 0 10px;
  border-radius: 24px;
  height: 24px;
  display: flex;
  align-items: center;
`;
